/**
 * Signing out means ending TWO sessions, and the order is not a detail.
 *
 * `POST /api/method/logout` must go first: it is authorised by the `sid` cookie, so after Clerk
 * has signed the user out the call would be unauthenticated and the Frappe session would simply
 * leak until it expired on its own — on a shared machine, that is somebody else's session.
 *
 * BOTH network calls are best-effort. Whatever either server does, the user must end up signed
 * out locally: a dead network or a rejected `clerkSignOut()` must not stop the sequence, because
 * being stranded signed-in — with no way out — is the worse failure than a session that outlives
 * its owner on the server side.
 */

import { clearFrappeSession, currentCsrfToken, setClerkTokenSource } from './frappeSession'
import { apiBase } from './apiBase'

/** Frappe's own logout endpoint — not a `quoflow.*` method, so it is spelled out here. */
const LOGOUT_URL = '/api/method/logout'

export async function signOutSequence(
  clerkSignOut: () => Promise<unknown>,
  onCleared?: () => void,
): Promise<void> {
  try {
    // Logout is a cookie-authenticated POST, so it needs CSRF like any other write. The token is
    // still held here — `clearFrappeSession()` below is what forgets it.
    const csrf = currentCsrfToken()
    await fetch(apiBase() + LOGOUT_URL, {
      method: 'POST',
      credentials: 'include',
      headers: csrf ? { 'X-Frappe-CSRF-Token': csrf } : {},
    })
  } catch {
    // Best effort. A dead network must not strand someone signed in to Clerk with no way out —
    // the Frappe session will expire on its own, and staying signed in is the worse failure.
  }
  clearFrappeSession()
  setClerkTokenSource(null)
  onCleared?.()
  try {
    await clerkSignOut()
  } catch {
    // Same reasoning as the fetch above: Clerk's own sign-out call can fail against a dead
    // network too, and a rejection here must not escape as an unhandled promise rejection at the
    // caller's bare `void signOutSequence(...)` — the user is already locally cleared above.
  }
}
